// @spec TSK-STR-009
// @spec TSK-STR-010
// @spec TSK-STR-011
// @spec TSK-STR-012
// @spec TSK-STR-013
// @spec TSK-STR-014
'use client';

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import type { RowSelectionState } from '@tanstack/react-table';
import { BarChart3, List, Plus } from 'lucide-react';
import * as React from 'react';
import { Toaster } from 'sonner';
import { z } from 'zod';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  BulkActionBar,
  BulkCloseDialog,
  BulkReassignDialog,
  BulkRescheduleDialog,
  taskKeys,
  TaskAnalyticsDashboard,
  TaskFilters,
  TaskStatusSummary,
  TasksTable,
  useTasks,
  useTaskSummary,
} from '@/features/task-console';
import type {
  SortByField,
  SortOrder,
  TaskFilterState,
  TaskPriority,
  TaskStatus,
  TaskSummary,
} from '@/features/task-console';
import { CreateTaskModal } from '@/features/tasks/components/create-task-modal';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';

// URL search params schema for validation
const taskConsoleSearchSchema = z.object({
  tab: z.enum(['list', 'analytics']).optional().default('list'),
  page: z.coerce.number().optional().default(1),
  pageSize: z.coerce.number().optional().default(20),
  sortBy: z
    .enum(['due_date', 'created_at', 'priority', 'status', 'title'])
    .optional()
    .default('created_at'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
  status: z
    .union([
      z.array(
        z.enum([
          'not_started',
          'in_progress',
          'blocked',
          'pending_review',
          'completed',
          'cancelled',
        ])
      ),
      z.enum(['not_started', 'in_progress', 'blocked', 'pending_review', 'completed', 'cancelled']),
    ])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  priority: z
    .union([
      z.array(z.enum(['critical', 'high', 'medium', 'low'])),
      z.enum(['critical', 'high', 'medium', 'low']),
    ])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  assigneeIds: z
    .union([z.array(z.string()), z.string()])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  sourceModule: z
    .enum(['ghg', 'esg', 'nis2', 'pol', 'ast', 'rsk', 'inc', 'vul', 'bcp', 'ai', 'custom'])
    .optional(),
  dueDateFrom: z.string().optional(),
  dueDateTo: z.string().optional(),
  workflowId: z.string().optional(),
  search: z.string().optional(),
  scope: z.enum(['my_team', 'organization']).optional(),
});

export const Route = createFileRoute('/_authenticated/tasks/console')({
  validateSearch: taskConsoleSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.taskProgress'),
  }),
  component: TasksConsolePage,
});

/**
 * Task Progress page - main entry point for task management.
 *
 * Provides:
 * - Filter bar with multiple filter criteria (status, priority, assignee, etc.)
 * - URL-based filter state for sharing and bookmarking
 * - Filter presets for saving common filter combinations
 * - Task list with pagination and sorting
 * - Create Task modal
 */
function TasksConsolePage() {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_authenticated/tasks/console' });
  const { organizations, isLoading: orgsLoading, hasClerkOrgMismatch, refetch: refetchOrgs } = useOrganizations();
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  // Bulk action state
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [isReassignDialogOpen, setIsReassignDialogOpen] = React.useState(false);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = React.useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = React.useState(false);

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Extract filter state from URL search params
  const filters: TaskFilterState = React.useMemo(
    () => ({
      status: search.status as TaskStatus[] | undefined,
      priority: search.priority as TaskPriority[] | undefined,
      assigneeIds: search.assigneeIds,
      sourceModule: search.sourceModule,
      dueDateFrom: search.dueDateFrom,
      dueDateTo: search.dueDateTo,
      workflowId: search.workflowId,
      search: search.search,
      scope: search.scope,
    }),
    [search]
  );

  // Fetch tasks with current filters
  const {
    data: tasksData,
    isLoading: tasksLoading,
    isFetching: tasksFetching,
  } = useTasks({
    orgId: activeOrg?.id ?? '',
    page: search.page,
    pageSize: search.pageSize,
    sortBy: search.sortBy as SortByField,
    sortOrder: search.sortOrder as SortOrder,
    ...filters,
    enabled: Boolean(activeOrg),
  });

  // Fetch task summary
  const { data: summaryData, isLoading: summaryLoading } = useTaskSummary({
    orgId: activeOrg?.id ?? '',
    enabled: Boolean(activeOrg),
  });

  // Update filters in URL
  const handleFiltersChange = React.useCallback(
    (newFilters: TaskFilterState) => {
      const searchUpdate = {
        ...search,
        ...newFilters,
        page: 1, // Reset to first page on filter change
      };
      void navigate({
        search: searchUpdate as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle sorting change
  const handleSortingChange = React.useCallback(
    (sortBy: SortByField, sortOrder: SortOrder) => {
      const searchUpdate = {
        ...search,
        sortBy,
        sortOrder,
      };
      void navigate({
        search: searchUpdate as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle pagination change
  const handlePaginationChange = React.useCallback(
    (page: number, pageSize: number) => {
      const searchUpdate = {
        ...search,
        page,
        pageSize,
      };
      void navigate({
        search: searchUpdate as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle row click - navigate to task detail
  const handleRowClick = React.useCallback((task: TaskSummary) => {
    void navigate({ to: '/tasks/$taskId', params: { taskId: task.id } });
  }, [navigate]);

  // Handle task creation success
  const handleTaskCreated = React.useCallback(() => {
    // Invalidate task queries to refresh the list
    void queryClient.invalidateQueries({ queryKey: taskKeys.all });
  }, [queryClient]);

  // Compute selected task IDs from row selection state
  const selectedTaskIds = React.useMemo(() => {
    return Object.keys(rowSelection).filter((key) => rowSelection[key]);
  }, [rowSelection]);

  // Compute selected task statuses map for bulk close validation
  const selectedTaskStatuses = React.useMemo(() => {
    const statusMap = new Map<string, TaskStatus>();
    if (tasksData?.items) {
      for (const task of tasksData.items) {
        if (rowSelection[task.id]) {
          statusMap.set(task.id, task.status);
        }
      }
    }
    return statusMap;
  }, [tasksData?.items, rowSelection]);

  // Handle clearing row selection
  const handleClearSelection = React.useCallback(() => {
    setRowSelection({});
  }, []);

  // Handle opening the reassign dialog
  const handleOpenReassignDialog = React.useCallback(() => {
    setIsReassignDialogOpen(true);
  }, []);

  // Handle opening the reschedule dialog
  const handleOpenRescheduleDialog = React.useCallback(() => {
    setIsRescheduleDialogOpen(true);
  }, []);

  // Handle opening the close dialog
  const handleOpenCloseDialog = React.useCallback(() => {
    setIsCloseDialogOpen(true);
  }, []);

  // Handle successful bulk reassignment
  const handleBulkReassignSuccess = React.useCallback(() => {
    // Clear selection after successful reassignment
    setRowSelection({});
  }, []);

  // Handle successful bulk reschedule
  const handleBulkRescheduleSuccess = React.useCallback(() => {
    // Clear selection after successful reschedule
    setRowSelection({});
  }, []);

  // Handle successful bulk close
  const handleBulkCloseSuccess = React.useCallback(() => {
    // Clear selection after successful close
    setRowSelection({});
  }, []);

  // Handle tab change
  const handleTabChange = React.useCallback(
    (tab: 'list' | 'analytics') => {
      void navigate({
        search: { ...search, tab } as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle analytics drilldown to task list with date filter
  const handleAnalyticsDateClick = React.useCallback(
    (date: string) => {
      void navigate({
        search: {
          ...search,
          tab: 'list',
          dueDateFrom: date,
          dueDateTo: date,
          page: 1,
        } as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle analytics drilldown to task list with module filter
  const handleAnalyticsModuleClick = React.useCallback(
    (module: string) => {
      void navigate({
        search: {
          ...search,
          tab: 'list',
          sourceModule: module,
          page: 1,
        } as never,
        replace: true,
      });
    },
    [navigate, search]
  );

  // Handle analytics drilldown to task detail
  const handleAnalyticsTaskClick = React.useCallback((taskId: string) => {
    void navigate({ to: '/tasks/$taskId', params: { taskId } });
  }, [navigate]);

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            {hasClerkOrgMismatch ? (
              <>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('console.orgSyncIssue')}
                </h2>
                <p className="mt-2 text-muted-foreground">
                  {t('console.orgSyncDescription')}
                </p>
                <Button
                  variant="outline"
                  onClick={() => void refetchOrgs()}
                  className="mt-4"
                >
                  {t('console.retry')}
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-foreground">
                  {t('console.noOrgSelected')}
                </h2>
                <p className="mt-2 text-muted-foreground">
                  {t('console.noOrgDescription')}
                </p>
                <Button
                  variant="outline"
                  onClick={() => void navigate({ to: '/settings/organizations' })}
                  className="mt-4"
                >
                  {t('console.goToOrganizations')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const activeTab = search.tab;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">{t('console.pageTitle')}</h1>
            <p className="mt-1 text-muted-foreground">{t('console.pageSubtitle', { orgName: activeOrg.name })}</p>
            {/*
              TODO(WP4-B): Render <GroupScopePill /> here once the task-console
              list/summary hooks expose `includeSubsidiaries` /
              `includedOrganizationCount`. Backend already returns these via
              `TaskConsoleListResponse`/`TaskConsoleSummaryResponse`
              (`RollupScopeMixin`). See
              `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
            */}
          </div>
          {activeTab === 'list' && (
            <Button
              onClick={() => {
                setIsCreateModalOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('console.createTask')}
            </Button>
          )}
        </header>

        {/* Tab Navigation */}
        <div className="flex gap-1 rounded-lg border bg-muted/30 p-1">
          <button
            onClick={() => {
              handleTabChange('list');
            }}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              activeTab === 'list'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
            )}
          >
            <List className="h-4 w-4" />
            {t('console.taskListTab')}
          </button>
          <button
            onClick={() => {
              handleTabChange('analytics');
            }}
            className={cn(
              'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              activeTab === 'analytics'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
            )}
          >
            <BarChart3 className="h-4 w-4" />
            {t('console.analyticsTab')}
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'list' ? (
          <>
            {/* Status Summary */}
            <TaskStatusSummary summary={summaryData} isLoading={summaryLoading} />

            {/* Filter Bar */}
            <TaskFilters filters={filters} onChange={handleFiltersChange} orgId={activeOrg.id} />

            {/* Tasks Table */}
            <TasksTable
              tasks={tasksData?.items ?? []}
              pagination={tasksData?.pagination}
              isLoading={tasksLoading || tasksFetching}
              sortBy={search.sortBy as SortByField}
              sortOrder={search.sortOrder as SortOrder}
              onSortingChange={handleSortingChange}
              onPaginationChange={handlePaginationChange}
              onRowClick={handleRowClick}
              enableRowSelection
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              orgId={activeOrg.id}
            />
          </>
        ) : (
          <TaskAnalyticsDashboard
            orgId={activeOrg.id}
            onDateClick={handleAnalyticsDateClick}
            onModuleClick={handleAnalyticsModuleClick}
            onTaskClick={handleAnalyticsTaskClick}
          />
        )}

        {/* Create Task Modal */}
        <CreateTaskModal
          orgId={activeOrg.id}
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
          }}
          onSuccess={handleTaskCreated}
        />

        {/* Bulk Reassign Dialog */}
        <BulkReassignDialog
          open={isReassignDialogOpen}
          onOpenChange={setIsReassignDialogOpen}
          selectedTaskIds={selectedTaskIds}
          orgId={activeOrg.id}
          onSuccess={handleBulkReassignSuccess}
        />

        {/* Bulk Reschedule Dialog */}
        <BulkRescheduleDialog
          open={isRescheduleDialogOpen}
          onOpenChange={setIsRescheduleDialogOpen}
          selectedTaskIds={selectedTaskIds}
          orgId={activeOrg.id}
          onSuccess={handleBulkRescheduleSuccess}
        />

        {/* Bulk Close Dialog */}
        <BulkCloseDialog
          open={isCloseDialogOpen}
          onOpenChange={setIsCloseDialogOpen}
          selectedTaskIds={selectedTaskIds}
          selectedTaskStatuses={selectedTaskStatuses}
          orgId={activeOrg.id}
          onSuccess={handleBulkCloseSuccess}
        />

        {/* Bulk Action Bar (fixed at bottom when tasks selected) */}
        <BulkActionBar
          selectedCount={selectedTaskIds.length}
          onClearSelection={handleClearSelection}
          onReassign={handleOpenReassignDialog}
          onReschedule={handleOpenRescheduleDialog}
          onClose={handleOpenCloseDialog}
        />

        {/* Toast notifications */}
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
